import { useEffect } from 'react';
import type { AuthenticatedUser } from '@frameio/graphql-client/generated';
import { useGetAccountsForRoutingQuery } from '@frameio/graphql-client/generated';
import { emitEvent } from '@frameio/telemetry';
import { useActiveAccountId } from '../ActiveEntitiesContext';
import { useSignupFromQueryParam } from './useSignupFromQueryParam';

const validAccountVersions = [3, 4] as const;
type ValidAccountVersion = (typeof validAccountVersions)[number];

const isValidAccountVersion = (
  accountVersion: number
): accountVersion is ValidAccountVersion => {
  return validAccountVersions.includes(accountVersion as ValidAccountVersion);
};

/**
 * Pulls the version for `accountId` out of a GetAccountsForRouting `me` payload,
 * checking both member accounts and inbox accounts. Returns `undefined` when the
 * version is not (yet) known, and throws on an unexpected version value.
 */
const selectAccountVersion = (
  me: AuthenticatedUser | undefined,
  accountId: string | undefined
): ValidAccountVersion | undefined => {
  // get account version for the account the user is a member of
  const memberAccountVersion = me?.accounts?.nodes?.find(
    (account) => account?.account?.id === accountId
  )?.account?.version;
  // get account version for inbox account if user is not a member of any accounts
  const inboxAccountVersion = me?.inboxAccounts?.nodes?.find(
    (account) => account?.id === accountId
  )?.version;

  const accountVersion = memberAccountVersion ?? inboxAccountVersion;

  if (!accountVersion) {
    return undefined;
  }

  if (!isValidAccountVersion(accountVersion)) {
    throw new Error(
      `Unknown account version ${accountVersion} for account ${accountId}`
    );
  }

  return accountVersion;
};

type UseAccountVersionParams = {
  accountId?: string;
};

export const useAccountVersion = (
  params?: UseAccountVersionParams
): ValidAccountVersion | undefined => {
  const activeAccountId = useActiveAccountId();
  const accountId = params?.accountId || activeAccountId;
  const signup = useSignupFromQueryParam();

  const { data } = useGetAccountsForRoutingQuery({
    variables: { first: 500, signup },
    skip: !accountId,
    nextFetchPolicy: 'cache-only',
  });

  return selectAccountVersion(data?.me as AuthenticatedUser, accountId);
};

type UseNetworkVerifiedAccountVersionParams = {
  accountId?: string;
  skip: boolean;
};

/**
 * Network-backed sibling of {@link useAccountVersion}.
 *
 * `useAccountVersion` reads `cache-only`, so once a stale
 * `version: 3` is cached for an account it is never re-fetched. This hook
 * is `network-only`, so it both re-fetches current value but also writes the fresh
 * value back into the cache, healing every `cache-only` reader.
 *
 * It is gated by `skip` so the network request only fires when a redirect is
 * imminent
 */
export const useNetworkVerifiedAccountVersion = ({
  accountId,
  skip,
}: UseNetworkVerifiedAccountVersionParams): ValidAccountVersion | undefined => {
  const signup = useSignupFromQueryParam();

  const { data } = useGetAccountsForRoutingQuery({
    variables: { first: 500, signup },
    skip: skip || !accountId,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!data) return;
    const version = selectAccountVersion(
      data?.me as AuthenticatedUser,
      accountId
    );
    if (version && version >= 4) {
      emitEvent('v3_redirect_decision', {
        outcome: 'prevented_stale_cache',
        accountId,
        verifiedVersion: version,
      });
    }
    // Only fire when a new completed result arrives (mirrors `onCompleted`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return selectAccountVersion(data?.me as AuthenticatedUser, accountId);
};
